import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTenant } from '../config/tenantConfig';
import { wines } from '../data/wines';
import { getMenuForTenant } from '../data/menus';
import { findMenuItem, resolveMenuWines } from '../utils/menuUtils';
import { trackEvent } from '../utils/analytics';
import { useScrollToTop } from '../hooks/useScrollToTop';
import ArrowLeftIcon from './icons/ArrowLeftIcon';
import WineCard from './wine/WineCard';

const MenuItemWines = () => {
  const { itemId } = useParams();
  const navigate = useNavigate();
  const tenant = useTenant();
  useScrollToTop(itemId);
  const menu = getMenuForTenant(tenant.id);
  const item = useMemo(() => findMenuItem(menu, itemId), [menu, itemId]);
  const { featured, others } = useMemo(
    () => (item ? resolveMenuWines(item, wines) : { featured: null, others: [] }),
    [item]
  );

  if (!item || !featured) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-wine-dark via-[#1e1a2e] to-wine-dark py-8 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-white text-xl mb-6">
            Não encontramos vinhos para este item do cardápio.
          </p>
          <button
            type="button"
            onClick={() => navigate('/menu')}
            className="text-wine-gold hover:underline"
          >
            Voltar ao cardápio
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-wine-dark via-[#1e1a2e] to-wine-dark py-8 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center mb-8">
          <button
            type="button"
            onClick={() => navigate('/menu')}
            className="flex items-center gap-2 text-white hover:text-wine-gold transition-colors"
          >
            <ArrowLeftIcon />
            <span className="text-lg">Voltar</span>
          </button>
        </div>

        <div className="flex flex-col md:flex-row gap-6 mb-12 items-center md:items-start">
          <div className="w-full md:w-64 h-48 rounded-2xl overflow-hidden bg-[#3a3545] flex-shrink-0">
            <img
              src={item.image}
              alt={item.name}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          </div>
          <div className="max-w-2xl">
            <h1 className="text-white text-3xl md:text-4xl font-serif mb-3">{item.name}</h1>
            <p className="text-gray-400 text-base md:text-lg leading-relaxed">
              {item.description}
            </p>
          </div>
        </div>

        <section className="mb-12">
          <h2 className="text-wine-gold text-2xl font-serif mb-6 text-center">
            Sugestão do sommelier
          </h2>
          <div
            className="max-w-sm mx-auto ring-2 ring-wine-gold/60 rounded-2xl"
            onClickCapture={() =>
              trackEvent('menu_featured_wine_click', {
                tenant: tenant.id,
                item: item.id,
                wine: featured.slug,
              })
            }
          >
            <WineCard wine={featured} />
          </div>
        </section>

        {others.length > 0 && (
          <section className="mb-12">
            <h2 className="text-white text-2xl font-serif mb-6 text-center">
              Outras opções
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {others.map((wine) => (
                <div
                  key={wine.slug}
                  onClickCapture={() =>
                    trackEvent('menu_other_wine_click', {
                      tenant: tenant.id,
                      item: item.id,
                      wine: wine.slug,
                    })
                  }
                >
                  <WineCard wine={wine} />
                </div>
              ))}
            </div>
          </section>
        )}

        <div className="text-center pt-4 pb-8 border-t border-gray-700/60">
          <p className="text-gray-400 text-sm md:text-base mb-4">
            Não encontrou o que procurava?
          </p>
          <button
            type="button"
            onClick={() => {
              trackEvent('menu_see_all_wines_click', {
                tenant: tenant.id,
                item: item.id,
              });
              navigate('/wines/grape');
            }}
            className="inline-flex items-center justify-center px-6 py-3 border-2 border-wine-gold rounded-2xl text-wine-gold hover:bg-wine-gold/10 transition-colors text-sm md:text-base tracking-wide uppercase"
          >
            Ver todos os vinhos da adega
          </button>
        </div>
      </div>
    </div>
  );
};

export default MenuItemWines;
